import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import RecordPreviewModal from '../components/RecordPreviewModal';
import { API_BASE_URL } from '../api/client';

function AdminDashboard() {
  const [scanLogs, setScanLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [threatOnly, setThreatOnly] = useState(false);
  const [datePeriod, setDatePeriod] = useState(30);
  const [previewLog, setPreviewLog] = useState(null);

  const token = localStorage.getItem('token');

  const loadScanLogs = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/expenses/admin/scan-logs?page=${page}&limit=20&threatOnly=${threatOnly}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Access denied or failed to load logs');
      const data = await response.json();
      setScanLogs(data.data || []);
    } catch (err) {
      console.error('Failed to load scan logs:', err);
      setScanLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, threatOnly, token]);

  const loadStats = useCallback(async () => {
    try {
      const url = `${API_BASE_URL}/expenses/admin/scan-stats?days=${datePeriod}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [datePeriod, token]);

  const loadHealth = useCallback(async () => {
    try {
      const url = `${API_BASE_URL}/system/health`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHealth(data);
      }
    } catch (err) {
      console.error('Failed to load health:', err);
    }
  }, [token]);

  useEffect(() => {
    loadScanLogs();
    loadStats();
    loadHealth();
  }, [loadScanLogs, loadStats, loadHealth]);

  const getThreatColor = (detected) => {
    return detected ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300';
  };

  const threatRate = stats?.total_scans
    ? Math.round(((stats.threats_detected || 0) / stats.total_scans) * 100)
    : 0;

  const statusPill = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'passed') return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
    if (normalized === 'blocked') return 'bg-rose-100 text-rose-800 border border-rose-200';
    if (normalized === 'error') return 'bg-amber-100 text-amber-800 border border-amber-200';
    return 'bg-slate-100 text-slate-700 border border-slate-200';
  };

  const periodOptions = [
    { value: 7, label: '7D' },
    { value: 30, label: '30D' },
    { value: 90, label: '90D' },
    { value: 365, label: '1Y' },
  ];

  const refreshAll = () => {
    loadScanLogs();
    loadStats();
    loadHealth();
  };

  return (
    <Layout pageTitle="Security Command Center" pageSubtitle="Threat monitoring, scan telemetry, and system health in one place">
      <div className="space-y-6">
        <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 text-white shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(56,189,248,0.28),_transparent_55%),radial-gradient(circle_at_bottom_left,_rgba(251,113,133,0.2),_transparent_55%)]" />
          <div className="relative p-6 md:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Security Operations</p>
                <h2 className="mt-3 text-2xl font-black tracking-tight md:text-3xl">Scan Intelligence and Risk Watch</h2>
                <p className="mt-3 max-w-2xl text-sm text-slate-300 md:text-base">
                  Review malware detections, scanner reliability, and infrastructure health without context switching.
                </p>
              </div>
              <div className="grid w-full max-w-sm grid-cols-2 gap-3">
                <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/10 p-4">
                  <p className="text-xs uppercase tracking-wide text-cyan-200">Threat Rate</p>
                  <p className="mt-1 text-2xl font-extrabold">{threatRate}%</p>
                </div>
                <div className="rounded-2xl border border-slate-600 bg-slate-900/70 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-300">Open Alerts</p>
                  <p className="mt-1 text-2xl font-extrabold">{health?.notifications?.unread_notifications || 0}</p>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={refreshAll}
                className="rounded-xl bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300"
              >
                Refresh Security Data
              </button>
              <button
                onClick={() => setThreatOnly((prev) => !prev)}
                className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${threatOnly ? 'border-rose-300 bg-rose-400/20 text-rose-100' : 'border-slate-500 bg-transparent text-slate-100 hover:border-slate-300'}`}
              >
                {threatOnly ? 'Threat Filter: On' : 'Threat Filter: Off'}
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Total Scans</p>
            <p className="mt-2 text-3xl font-black text-slate-900">{stats?.total_scans || 0}</p>
            <p className="mt-2 text-xs text-slate-500">Across selected period</p>
          </article>
          <article className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-700">Passed</p>
            <p className="mt-2 text-3xl font-black text-emerald-800">{stats?.scans_passed || 0}</p>
            <p className="mt-2 text-xs text-emerald-700">Clean file scans</p>
          </article>
          <article className="rounded-2xl border border-rose-200 bg-rose-50 p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-rose-700">Threats Blocked</p>
            <p className="mt-2 text-3xl font-black text-rose-800">{stats?.threats_detected || 0}</p>
            <p className="mt-2 text-xs text-rose-700">Potentially malicious uploads</p>
          </article>
          <article className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-amber-700">Scan Errors</p>
            <p className="mt-2 text-3xl font-black text-amber-800">{stats?.scan_errors || 0}</p>
            <p className="mt-2 text-xs text-amber-700">Requires scanner review</p>
          </article>
          <article className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-indigo-700">Unique Users</p>
            <p className="mt-2 text-3xl font-black text-indigo-800">{stats?.unique_users || 0}</p>
            <p className="mt-2 text-xs text-indigo-700">Scanned by active users</p>
          </article>
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Database Health</p>
            <p className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${health?.database?.healthy ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
              {health?.database?.healthy ? 'Healthy' : 'Error'}
            </p>
            <p className="mt-3 text-xs text-slate-500">Last check: {health?.checked_at ? new Date(health.checked_at).toLocaleString() : 'N/A'}</p>
          </article>
          <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Scanner Runtime</p>
            <p className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${health?.clamav?.running ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
              {health?.clamav?.running ? 'ClamAV Running' : 'ClamAV Stopped'}
            </p>
            <p className="mt-3 text-xs text-slate-500">Keep scanner online for real-time upload validation.</p>
          </article>
          <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Users in System</p>
            <p className="mt-2 text-3xl font-black text-slate-900">{health?.users?.total_jobs || 0}</p>
            <p className="mt-3 text-xs text-slate-500">Contributing to current security telemetry.</p>
          </article>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Filters</p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">Review Window and Threat Mode</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {periodOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setDatePeriod(option.value)}
                  className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${datePeriod === option.value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  {option.label}
                </button>
              ))}
              <button
                onClick={loadStats}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700"
              >
                Apply Period
              </button>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Scan Logs</p>
              <h3 className="text-lg font-bold text-slate-900">Security Scan Ledger {threatOnly ? '(Threats Only)' : ''}</h3>
            </div>
            <p className="text-xs text-slate-500">Page {page}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Date</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">User</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">File</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Scanner</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Threat</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Details</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {scanLogs.map((log) => (
                  <tr
                    key={log.id}
                    className={`${getThreatColor(log.threat_detected)} border-l-4 ${log.threat_detected ? 'border-rose-500' : 'border-emerald-500'} align-top`}
                  >
                    <td className="px-5 py-4 text-sm text-slate-800">
                      <p className="font-medium">{new Date(log.created_at).toLocaleDateString()}</p>
                      <p className="text-xs text-slate-500">{new Date(log.created_at).toLocaleTimeString()}</p>
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <p className="font-semibold text-slate-900">{log.user_name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500">{log.user_email || '-'}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-700 max-w-[240px] truncate" title={log.file_name}>
                      {log.file_name || '-'}
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
                        {log.scanner_type || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusPill(log.scan_status)}`}>
                        {log.scan_status ? log.scan_status.charAt(0).toUpperCase() + log.scan_status.slice(1) : 'Unknown'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm">
                      {log.threat_detected ? (
                        <span className="inline-flex rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800">Detected</span>
                      ) : (
                        <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">Clean</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600 max-w-[260px] truncate" title={log.threat_details}>
                      {log.threat_details || 'No threat details'}
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <button
                        onClick={() => setPreviewLog(log)}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {scanLogs.length === 0 && (
            <div className="px-6 py-10 text-center">
              <p className="text-sm font-semibold text-slate-700">{loading ? 'Loading scan logs...' : 'No scan logs found'}</p>
              <p className="mt-1 text-xs text-slate-500">Try adjusting threat filter or date period.</p>
            </div>
          )}
        </section>

        <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="rounded-xl bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous Page
          </button>
          <p className="text-sm font-semibold text-slate-700">Current Page: {page}</p>
          <button
            onClick={() => setPage(page + 1)}
            disabled={scanLogs.length < 20}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next Page
          </button>
        </section>

        <RecordPreviewModal
          open={Boolean(previewLog)}
          title={previewLog ? `Scan Log #${previewLog.id}` : 'Scan Log'}
          subtitle="Security scan log preview"
          onClose={() => setPreviewLog(null)}
          fields={previewLog ? [
            { label: 'Created', value: new Date(previewLog.created_at).toLocaleString() },
            { label: 'User Name', value: previewLog.user_name || 'Unknown' },
            { label: 'User Email', value: previewLog.user_email || '-' },
            { label: 'File Name', value: previewLog.file_name || '-' },
            { label: 'Scanner', value: previewLog.scanner_type || '-' },
            { label: 'Scan Status', value: previewLog.scan_status || '-' },
            { label: 'Threat Detected', value: previewLog.threat_detected ? 'Yes' : 'No' },
            { label: 'Threat Details', value: previewLog.threat_details || 'No threat details' },
          ] : []}
        />
      </div>
    </Layout>
  );
}

export default AdminDashboard;
